import type { PlayerLocationDeltas } from '@repo/types/maps/map-types';
import { gameCoordsToLatLng, remapGameCoords } from '@repo/types/maps/map-utils';
import type {
    GameSessionInternal,
    SessionEventCategory,
    SessionEventInternal,
} from '@repo/types/sessions/session-types';
import { hasEventPosition, isGameEvent } from '@repo/utility/events';

/**
 * Heuristic: if all trail points fall within geographic coordinate bounds,
 * they're likely already lat/lng and don't need game-to-map transformation.
 */
export function looksLikeLatLng(trail: GameSessionInternal['trail']): boolean {
    return trail.length > 0 && trail.every(p => p.x >= -180 && p.x <= 180 && p.y >= -90 && p.y <= 90);
}

/**
 * Transform session trail coordinates using the same pipeline as live player location
 */
export const transformTrailCoordinates = (
    session: GameSessionInternal,
    deltas: PlayerLocationDeltas | null,
): [number, number][] => {
    if (looksLikeLatLng(session.trail)) {
        return session.trail.map(point => [point.x, point.y] as [number, number]);
    }

    if (!deltas?.deltas) {
        return session.trail.map(point => [point.x, point.y] as [number, number]);
    }

    return session.trail.map(point => {
        try {
            const gameCoords = { x: point.x, y: point.y, z: point.z };
            const remappedCoords = remapGameCoords(gameCoords, deltas.deltas.gameCoordsMap);
            const latLng = gameCoordsToLatLng(remappedCoords, deltas.deltas);
            if (!latLng) return [point.x, point.y] as [number, number];
            return [latLng.lng, latLng.lat] as [number, number];
        } catch {
            return [point.x, point.y] as [number, number];
        }
    });
};

/**
 * Transform a single game coordinate point using the same pipeline as trail coordinates.
 * Returns [lng, lat] or the raw [x, y] if transformation is not possible.
 */
export function transformGameCoord(
    point: { x: number; y: number; z: number },
    deltas: PlayerLocationDeltas | null,
    isLatLng: boolean,
): [number, number] {
    if (isLatLng) {
        return [point.x, point.y];
    }

    if (!deltas?.deltas) {
        return [point.x, point.y];
    }

    try {
        const remapped = remapGameCoords(point, deltas.deltas.gameCoordsMap);
        const latLng = gameCoordsToLatLng(remapped, deltas.deltas);
        if (latLng) {
            return [latLng.lng, latLng.lat];
        }
    } catch {
        // Fall through with raw coordinates
    }

    return [point.x, point.y];
}

/**
 * Transform session event coordinates using the same pipeline as trail coordinates.
 * Filters to only game events with valid positions.
 */
export const transformEventCoordinates = (
    events: SessionEventInternal[],
    trail: GameSessionInternal['trail'],
    deltas: PlayerLocationDeltas | null,
): Array<{ coordinate: [number, number]; category: SessionEventCategory }> => {
    const isLatLng = looksLikeLatLng(trail);

    const results: Array<{ coordinate: [number, number]; category: SessionEventCategory }> = [];

    for (const event of events) {
        if (!isGameEvent(event.eventType)) continue;
        if (!hasEventPosition(event.x, event.y, event.z)) continue;

        results.push({
            coordinate: transformGameCoord(event, deltas, isLatLng),
            category: event.eventCategory,
        });
    }

    return results;
};
